<template>
  <div v-if="hasBackupCareRequests">
    <BackupCareRequestSelection :scCareSessions="scCareSessions" />
  </div>
  <div v-else>
    <CareTypeCardGroup
      v-if="!isLoading"
      :careTypes="careTypes"
      :headingText="'What type of care do you currently need?'"
      @select="saveCareType"
    />
  </div>
</template>

<script lang="ts">
import { CareType } from '@/types/care_type';
import CareTypeCardGroup from '@/components/careType/CareTypeCardGroup.vue';
import { sendSegmentEventWithTimeout } from '@/lib/segment';
import OnboardingMutations from '@/graphql/onboarding/mutations';
import { SC_CARE_SESSIONS_QUERY } from '@/graphql/backup_care/queries';
import BackupCareRequestSelection from './BackupCareRequestSelection.vue';
import { redirectToJobPost } from '@/helpers/jobPostRedirect';

export default {
  name: 'CorporateCareTypeSelection',
  components: {
    CareTypeCardGroup,
    BackupCareRequestSelection,
  },
  data() {
    return {
      isLoading: false,
      selectedCareType: '',
      hasBackupCareRequests: false,
      scCareSessions: [] as any[],
    };
  },
  computed: {
    corporateCareTypeTitles(): string[] {
      return ['CHILD_CARE', 'COMPANION_CARE', 'PET_CARE', 'SPECIAL_NEEDS', 'HOUSEKEEPING'];
    },
    careTypes(): string[] {
      return this.corporateCareTypeTitles;
    },
  },
  async beforeMount() {
    await this.checkForBackupCareRequests();
  },
  methods: {
    async saveCareType(careType: CareType | 'CHILD_CARE') {
      this.selectedCareType = careType;
      try {
        await this.$apollo.mutate({
          mutation: OnboardingMutations.COMPLETE_CARE_TYPE_TASK,
          variables: { careType: this.selectedCareType === 'CHILD_CARE' ? 'BABYSITTER' : this.selectedCareType },
        });
        await sendSegmentEventWithTimeout('Care Type Selected', { care_type: this.selectedCareType });
        if (!this.hasBackupCareRequests) {
          redirectToJobPost(this.selectedCareType as CareType | 'CHILD_CARE', this.$router, {
            returnPath: '/corporate-onboarding/care-type',
          });
        }
      } catch (error) {
        this.handleError();
      }
    },
    async checkForBackupCareRequests() {
      this.isLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: SC_CARE_SESSIONS_QUERY,
          fetchPolicy: 'cache-first',
        });
        const bhReservationRequests = data.corp.backupCare.bhReservationRequests || [];
        this.scCareSessions = bhReservationRequests.flatMap((reservation: any) => reservation.scCareSessions);
        const currentDateISO = new Date().toISOString();
        const activeSessions = this.scCareSessions.filter((session: any) => session.start >= currentDateISO);
        this.hasBackupCareRequests = activeSessions.length > 0;
        if (this.hasBackupCareRequests) {
          await this.autoSetCareTypeForBUCReservations();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    async autoSetCareTypeForBUCReservations(): Promise<void> {
      const currentDateISO = new Date().toISOString();
      const activeSessions = this.scCareSessions.filter((session: any) => session.start >= currentDateISO);
      const babysitterCareType: boolean = activeSessions.some((session) => [CareType.BABYSITTER].includes(session?.careType));
      const companionCareType: boolean = activeSessions.some((session) => [CareType.COMPANION_CARE].includes(session?.careType));

      const careType: CareType = !babysitterCareType && companionCareType ? CareType.COMPANION_CARE : CareType.BABYSITTER;

      await this.saveCareType(careType);
    },
    handleError() {
      this.$router.push('/something-went-wrong?retry=true');
    },
  },
};
</script>
